/** axios 1.x 断网错误码；0.26 无该常量，使用字符串字面量兼容 */
const AXIOS_ERR_NETWORK = 'ERR_NETWORK';

const REQUEST_NETWORK_ERROR_MESSAGE_SHOWN = 'requestNetworkErrorMessageShown';

interface AxiosErrorLike {
  message: string;
  code?: string;
  response?: unknown;
  isAxiosError: boolean;
}

/**
 * 兼容 axios 0.26 / 1.x 的 Axios 错误判断（0.26 无 isAxiosError 运行时导出）。
 */
function isAxiosErrorLike(error: unknown): error is AxiosErrorLike {
  return !!error
    && typeof error === 'object'
    && (error as AxiosErrorLike).isAxiosError === true;
}

/**
 * 判断是否为 Axios 断网/网络不可用错误。
 * axios 0.26 断网时 code 为 null、message 为 "Network Error"；axios 1.x 为 code === ERR_NETWORK。
 */
export function isAxiosNetworkError(error: unknown): boolean {
  if (!isAxiosErrorLike(error)) {
    return false;
  }
  if (error.code === AXIOS_ERR_NETWORK) {
    return true;
  }
  return !error.response && error.message === 'Network Error';
}

/**
 * 判断浏览器原生 Fetch 抛出的网络异常，兼容不同内核附带请求域名的错误文案。
 */
export function isFetchNetworkError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  const hasNetworkMessage = /(?:failed to fetch|network\s*error|load failed)/i.test(error.message);
  return hasNetworkMessage && (error.name === 'TypeError' || /failed to fetch/i.test(error.message));
}

/**
 * 统一判断 Axios 和 Fetch 的断网异常，避免订阅 SDK 的原始请求信息进入 UI。
 */
export function isRequestNetworkError(error: unknown): boolean {
  return isAxiosNetworkError(error) || isFetchNetworkError(error);
}

function getRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' ? value as Record<string, unknown> : undefined;
}

/**
 * 读取请求错误的业务码，兼容 Axios response.data、请求库 data 和业务层包装错误。
 */
export function resolveRequestErrorCode(error: unknown): string {
  const errorRecord = getRecord(error);
  if (!errorRecord) return '';
  const responseRecord = getRecord(errorRecord.response);
  const responseData = getRecord(responseRecord?.data);
  const directData = getRecord(errorRecord.data);
  const code = responseData?.code ?? directData?.code ?? errorRecord.code;
  return typeof code === 'number' || typeof code === 'string' ? String(code) : '';
}

/**
 * 将请求错误转为可传给 message 展示的文案（i18n key 或后端 message）。
 */
export function resolveRequestErrorMessage(error: unknown): string {
  if (isRequestNetworkError(error)) {
    return 'RoboNeo_Web_network_fail_retry';
  }
  if (typeof error === 'string') {
    return error;
  }
  if (error && typeof error === 'object') {
    const record = error as Record<string, unknown>;
    const response = getRecord(record.response);
    const responseData = getRecord(response?.data);
    const data = getRecord(record.data);
    if (typeof record.error_msg === 'string' && record.error_msg.trim()) {
      return record.error_msg;
    }
    // 业务接口失败时优先使用响应里的可见文案，避免 Axios 的通用错误覆盖后端提示。
    if (typeof responseData?.message === 'string' && responseData.message.trim()) {
      return responseData.message;
    }
    if (typeof data?.message === 'string' && data.message.trim()) {
      return data.message;
    }
    if (error instanceof Error && error.message) {
      return error.message;
    }
  }
  return '';
}

/**
 * 请求层是否已弹出过断网 toast，避免拦截器与业务 catch 重复提示。
 */
export function isRequestNetworkErrorMessageShown(error: unknown): boolean {
  return typeof error === 'object' && error !== null
    && (error as Record<string, unknown>)[REQUEST_NETWORK_ERROR_MESSAGE_SHOWN] === true;
}

/**
 * 断网时在请求层弹出一次友好的网络重试提示。
 */
export function notifyRequestNetworkErrorOnce(error: unknown, showMessage: (key: string) => void): void {
  if (!isRequestNetworkError(error) || isRequestNetworkErrorMessageShown(error)) {
    return;
  }
  const text = resolveRequestErrorMessage(error);
  if (!text) {
    return;
  }
  showMessage(text);
  if (typeof error === 'object' && error !== null) {
    const errorRecord = error as Record<string, unknown>;
    errorRecord[REQUEST_NETWORK_ERROR_MESSAGE_SHOWN] = true;
  }
}
